import { flip, inline, offset, shift, type Placement } from '@floating-ui/dom';
import { injectService } from '@patsnap/tahiti-core';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import { FloatUIPositioningStrategy } from '@telecom/shared/services/popover/impls/positioning-strategies';
import { PopoverRegistry } from '@telecom/shared/services/popover/popover-registry';
import CustomTooltip from '@telecom/shared/components/tooltip/CustomTooltip.vue';
import { createApp } from 'vue';
import { SafeHtmlPlugin } from '@telecom/shared/plugins/safe-html';

function positioningStrategy(reference: HTMLElement) {
    return new FloatUIPositioningStrategy({
        placement: (reference.dataset.tooltipPlacement as Placement) || 'top',
        middleware: [
            inline(),
            offset(4),
            flip({
                boundary: 'clippingAncestors',
                fallbackPlacements: ['bottom', 'right', 'left'],
            }),
            shift({
                boundary: 'clippingAncestors',
                padding: 5,
            }),
        ],
    });
}

function renderTooltip(reference: HTMLElement) {
    let div = undefined;
    const app = createApp(CustomTooltip, {
        title: reference.dataset.tooltip as string,
        theme: reference.dataset.tooltipTheme as 'light' | 'dark',
    });

    app.use(SafeHtmlPlugin);

    return {
        mount() {
            div = document.createElement('div');
            app.mount(div);
            return div.firstChild as HTMLElement;
        },
        dispose() {
            div = undefined;
            app.unmount();
        },
    };
}

export function useTooltip() {
    const popoverRegistry = injectService<PopoverRegistry>(PopoverRegistry);

    const { disposes } = useDisposes();

    disposes.push(
        popoverRegistry.register({
            selector: '[data-tooltip]',
            trigger: 'hover',
            render: renderTooltip,
            positioningStrategy,
        }),
    );
}
